import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import { CalendarSlot, Meeting, MeetingStatus } from "../../../types";
import { useListPatients } from "../../../store";
import { EditMeetingModal, MeetingInfoModal } from "../../meeting-management";
import MonthlyMeetingInfo from "./monthly-meeting-info";

interface MonthlyViewProps {
  meetingList: CalendarSlot[];
  date: Date;
  visibleWeekDays?: number[];
  isLoading?: boolean;
  onSelectTimeslot?: (start: Date, end: Date) => void;
}

const MAX_VISIBLE_EVENT_ROWS = 2;
const TWO_COLUMN_DAY_CELL_MIN_WIDTH = 220;

const getNextHalfHour = (now: Date): Date => {
  const next = new Date(now);
  next.setSeconds(0, 0);

  const minutes = next.getMinutes();
  const remainder = minutes % 30;
  const minutesToAdd = remainder === 0 ? 30 : 30 - remainder;

  next.setMinutes(minutes + minutesToAdd);
  return next;
};

const WeeklyColumns: ReadonlyArray<{ key: number; label: string }> = [
  { key: 0, label: "Sun" },
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
];

const MonthlyView: React.FC<MonthlyViewProps> = ({
  meetingList,
  date,
  visibleWeekDays = [1, 2, 3, 4, 5],
  isLoading = false,
  onSelectTimeslot,
}) => {
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [gridWidth, setGridWidth] = useState(0);

  const gridRef = useRef<HTMLDivElement | null>(null);

  const { data: patients = [], isLoading: patientsLoading } = useListPatients();

  const selectedMeeting = useMemo(
    () => meetingList.find((meeting) => meeting.meetingId === selectedMeetingId) ?? null,
    [meetingList, selectedMeetingId],
  );

  const patientLabelById = useMemo(() => {
    return patients.reduce(
      (acc, patient) => {
        acc[patient.patientId] = patient.displayId;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [patients]);

  const dayCells = useMemo(() => {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const firstVisibleDay = startOfWeek(monthStart, { weekStartsOn: 0 });
    const lastVisibleDay = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const cells: Date[] = [];

    let current = firstVisibleDay;
    while (current <= lastVisibleDay) {
      cells.push(current);
      current = addDays(current, 1);
    }

    return cells;
  }, [date]);

  const visibleColumns = useMemo(() => {
    return WeeklyColumns.filter((column) => visibleWeekDays.includes(column.key));
  }, [visibleWeekDays]);

  const visibleDayCells = useMemo(() => {
    return dayCells.filter((day) => visibleWeekDays.includes(day.getDay()));
  }, [dayCells, visibleWeekDays]);

  const weeks = useMemo(() => {
    const columnsPerWeek = visibleColumns.length;
    const groupedWeeks: Date[][] = [];

    if (columnsPerWeek === 0) {
      return groupedWeeks;
    }

    for (let index = 0; index < visibleDayCells.length; index += columnsPerWeek) {
      groupedWeeks.push(visibleDayCells.slice(index, index + columnsPerWeek));
    }

    return groupedWeeks;
  }, [visibleColumns.length, visibleDayCells]);

  const isTwoColumnLayout = useMemo(() => {
    if (visibleColumns.length === 0) {
      return false;
    }

    const estimatedDayCellWidth = gridWidth / visibleColumns.length;
    return estimatedDayCellWidth >= TWO_COLUMN_DAY_CELL_MIN_WIDTH;
  }, [gridWidth, visibleColumns.length]);

  const isCompressedMonth = useMemo(() => weeks.length >= 6, [weeks.length]);
  const maxVisibleEventsPerDay =
    MAX_VISIBLE_EVENT_ROWS * (isTwoColumnLayout ? 2 : 1);

  const meetingsByDay = useMemo(() => {
    const byDay: Record<string, CalendarSlot[]> = {};

    for (const meeting of meetingList) {
      const dayKey = format(new Date(meeting.startTime), "yyyy-MM-dd");
      if (!byDay[dayKey]) {
        byDay[dayKey] = [];
      }

      byDay[dayKey].push(meeting);
    }

    for (const dayKey of Object.keys(byDay)) {
      byDay[dayKey].sort(
        (left, right) =>
          new Date(left.startTime).getTime() - new Date(right.startTime).getTime(),
      );
    }

    return byDay;
  }, [meetingList]);

  const isLoadingData = isLoading || patientsLoading;

  useEffect(() => {
    if (!gridRef.current) {
      return;
    }

    const element = gridRef.current;
    const updateWidth = () => {
      setGridWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [visibleColumns.length]);

  const handleSelectMeeting = useCallback((meetingId: string) => {
    setSelectedMeetingId(meetingId);
  }, []);

  const handleCloseMeetingModal = useCallback(() => {
    setSelectedMeetingId(null);
  }, []);

  const handleOpenEditModal = useCallback((meeting: Meeting | null) => {
    setEditingMeeting(meeting);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingMeeting(null);
  }, []);

  const handleDayClick = useCallback(
    (day: Date) => {
      if (!onSelectTimeslot) {
        return;
      }

      const now = new Date();
      const today = startOfDay(now);
      if (day < today) {
        return;
      }

      let slotStart = new Date(day);
      slotStart.setHours(9, 0, 0, 0);

      if (isToday(day)) {
        slotStart = getNextHalfHour(now);
      }

      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + 30);

      onSelectTimeslot(slotStart, slotEnd);
    },
    [onSelectTimeslot],
  );

  const todayStart = useMemo(() => startOfDay(new Date()), []);

  if (isLoadingData) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          overflowX: "hidden",
          boxSizing: "border-box",
          px: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #DDD",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
            backgroundColor: "white",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  if (visibleColumns.length === 0) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          overflowX: "hidden",
          boxSizing: "border-box",
          px: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #DDD",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
            backgroundColor: "white",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No days selected
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        overflowX: "hidden",
        boxSizing: "border-box",
        px: 2,
        pb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #DDD",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
          backgroundColor: "white",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))`,
            borderBottom: "1px solid #E6E6E6",
            backgroundColor: "#FAFAFA",
          }}
        >
          {visibleColumns.map((column) => (
            <Box
              key={column.label}
              sx={{
                px: 1,
                py: 1,
                borderRight: "1px solid #EEE",
                textAlign: "center",
                "&:last-of-type": {
                  borderRight: "none",
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {column.label.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          ref={gridRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "grid",
            gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
          }}
        >
          {weeks.map((week, weekIndex) => (
            <Box
              key={`week-${weekIndex}`}
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))`,
                minHeight: 0,
              }}
            >
              {week.map((day, dayIndex) => {
                const key = format(day, "yyyy-MM-dd");
                const dayMeetings = meetingsByDay[key] || [];
                const visibleMeetingsForDay = dayMeetings.slice(0, maxVisibleEventsPerDay);
                const hiddenMeetingCount = Math.max(0, dayMeetings.length - maxVisibleEventsPerDay);
                const inCurrentMonth = isSameMonth(day, date);
                const todayCell = isToday(day);
                const isSelectableDay = !!onSelectTimeslot && day >= todayStart;

                return (
                  <Stack
                    key={key}
                    onClick={() => handleDayClick(day)}
                    sx={{
                      minHeight: 0,
                      height: "100%",
                      overflow: "hidden",
                      borderRight: "1px solid #C9D1D9",
                      borderBottom: "1px solid #C9D1D9",
                      borderLeft: dayIndex === 0 ? "1px solid #C9D1D9" : "none",
                      borderTop: weekIndex === 0 ? "1px solid #C9D1D9" : "none",
                      boxShadow: todayCell ? "inset 0 0 0 2px #1976d2" : "none",
                      boxSizing: "border-box",
                      px: 0.75,
                      py: 0.75,
                      backgroundColor: todayCell
                        ? "#eaf6ff"
                        : inCurrentMonth
                          ? "#FFFFFF"
                          : "#F7F7F7",
                      cursor: isSelectableDay ? "pointer" : "default",
                      transition: "background-color 0.15s ease-in-out",
                      "&:hover": isSelectableDay
                        ? {
                          backgroundColor: todayCell
                            ? "#dceeff"
                            : "rgba(25, 118, 210, 0.08)",
                        }
                        : undefined,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: todayCell ? 800 : 600,
                          color: inCurrentMonth ? "text.primary" : "text.disabled",
                        }}
                      >
                        {format(day, "d")}
                      </Typography>
                      {todayCell && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: "#1976d2", fontSize: "0.62rem", lineHeight: 1 }}
                        >
                          Today
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gap: isCompressedMonth ? 0.35 : 0.5,
                        gridTemplateColumns: isTwoColumnLayout ? "repeat(2, minmax(0, 1fr))" : "1fr",
                        alignContent: "start",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      {visibleMeetingsForDay.map((meeting) => {
                        const patientLabel = patientLabelById[meeting.patientId] || "Unknown";

                        return (
                          <MonthlyMeetingInfo
                            key={`${meeting.meetingId}-${meeting.startTime}`}
                            meeting={meeting}
                            patientLabel={patientLabel}
                            onSelectMeeting={handleSelectMeeting}
                            compact={isCompressedMonth}
                          />
                        );
                      })}

                      {hiddenMeetingCount > 0 && (
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: "primary.main",
                            px: 0.25,
                            gridColumn: "1 / -1",
                          }}
                        >
                          +{hiddenMeetingCount} more
                        </Typography>
                      )}

                      {dayMeetings.length === 0 && (
                        <Box sx={{ minHeight: 0 }} />
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {selectedMeeting && (
        <MeetingInfoModal
          open={selectedMeeting != null}
          handleClose={handleCloseMeetingModal}
          meetingId={selectedMeeting.meetingId}
          onEditMeeting={handleOpenEditModal}
        />
      )}

      {editingMeeting && (
        <EditMeetingModal
          open={editingMeeting != null}
          handleClose={handleCloseEditModal}
          meetingId={editingMeeting.meetingId}
        />
      )}
    </Stack>
  );
};

export default MonthlyView;
